import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import TextInput from './TextInput';
import StyledPhoneInput from './PhoneInput';
import { getInputName } from '../../utils/form/clientValues';

// Restyles the shared field components from their side label to a stacked one,
// scoped to this grid so nothing outside it is affected. The doubled `&&`
// outranks form.css's own `.full-page div.input ...` rules, which carry one
// element more than a single `&` here would.
const fieldGridSx: SxProps<Theme> = {
  display: 'grid',
  // minmax(0, 1fr) so the columns can shrink past the inputs' min-content
  // width; they collapse to one column once two no longer fit.
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 1.5,
  '@media (max-width: 599.98px)': {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
  '&& .input': {
    flexDirection: 'column',
    alignItems: 'stretch',
    width: 'auto',
    margin: 0,
    gap: 0.75,
  },
  '&& .input label': {
    width: 'auto',
    background: 'transparent',
    border: 'none',
    padding: 0,
  },
  '&& .input label .MuiTypography-root': {
    lineHeight: 'normal',
    typography: 'caption',
    fontWeight: 600,
  },
  '&& .input input, && .input .react-tel-input .form-control': {
    padding: '13px 14px',
    height: 'auto',
    fontSize: 'body2.fontSize',
    lineHeight: 'normal',
    borderRadius: '5px',
    border: (theme: Theme) => `1px solid ${theme.palette.darkerLightGrey.main}`,
    background: (theme: Theme) => theme.palette.common.white,
  },
  '&& .input .react-tel-input .form-control': {
    paddingLeft: '52px',
  },
  '&& .input.error input, && .input.error .react-tel-input .form-control': {
    borderColor: (theme: Theme) => theme.palette.error.main,
    outlineColor: (theme: Theme) => theme.palette.error.main,
  },
  '&& .phone-number-placeholder': {
    display: 'none',
  },
};

interface Props {
  namePrefix: string[];
  disabled: boolean;
  disablePhone?: boolean;
  hideBirthday: boolean;
}

export default function ClientFields({
  namePrefix,
  disabled,
  disablePhone = false,
  hideBirthday,
}: Props) {
  return (
    <Box sx={fieldGridSx}>
      <TextInput
        label="First name *"
        name={getInputName(namePrefix, 'firstName')}
        disabled={disabled}
        placeholder="John"
      />
      <TextInput
        label="Last name"
        name={getInputName(namePrefix, 'lastName')}
        disabled={disabled}
        placeholder="Doe"
      />
      <TextInput
        label="Email"
        name={getInputName(namePrefix, 'email')}
        type="email"
        placeholder="Email"
        disabled={disabled}
      />
      <StyledPhoneInput
        label="Phone *"
        name={getInputName(namePrefix, 'phoneNumber')}
        placeholder="Phone Number"
        disabled={disabled || disablePhone}
      />
      {!hideBirthday && (
        <TextInput
          label="Birthday"
          name={getInputName(namePrefix, 'dob')}
          type="date"
          disabled={disabled}
        />
      )}
    </Box>
  );
}
